import React from 'react';
import { ArrowRight, MessageCircle, CreditCard, RefreshCw, Activity, Stethoscope, FileText, DollarSign, Snowflake } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../lib/auth-context';
import { openWebRedirect } from '../../lib/api';

interface WhyChooseSectionProps {
  title: string;
  content: string;
  sections: Array<{
    icon: string;
    title: string;
    description: string;
    list: Array<{
      icon: string;
      content: string;
    }>;
  }>;
}

const advantages = [
  {
    icon: Activity,
    text: "Epidemies & Covid-19 couverts : frais medicaux, hospitalisation et rapatriement.",
    color: 'text-rose-500 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
  },
  {
    icon: MessageCircle,
    text: "Contact facile et rapide avec les conseillers Globe PVT via notre messagerie privee.",
    color: 'text-sky-500 dark:text-sky-400',
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    link: { url: 'https://pvtistes.net/forum/private.php?do=newpm&u=44868', label: 'Envoyer un message' },
  },
  {
    icon: CreditCard,
    text: "Possibilite de payer votre assurance en 2, 3 ou 4 fois.",
    color: 'text-emerald-500 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    link: { url: 'https://pvtistes.net/payez-votre-assurance-globe-pvt-en-plusieurs-fois/', label: 'En savoir plus' },
  },
  {
    icon: RefreshCw,
    text: "Remboursement des mois non utilises en cas de retour definitif.",
    color: 'text-amber-500 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    link: { url: 'https://pvtistes.net/dossiers/assurance-pvt-whv-infos/6/', label: 'En savoir plus' },
  },
  {
    icon: FileText,
    text: "Demandes de remboursement en quelques clics grace a une interface web dediee.",
    color: 'text-teal-500 dark:text-teal-400',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    link: { url: 'https://pvtistes.net/se-faire-rembourser-globe-pvt/', label: 'Voir le tutoriel' },
  },
  {
    icon: Snowflake,
    text: "Couverture de nombreux sports : plongee, ski sur piste, surf, randonnee...",
    color: 'text-cyan-500 dark:text-cyan-400',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    link: { url: 'https://pvtistes.net/assurance-pvt/#assurance-docs', label: 'Lire les conditions' },
  },
  {
    icon: DollarSign,
    text: "Des tarifs ultra-competitifs, a partir de 31,84 EUR/mois !",
    color: 'text-green-500 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
  },
  {
    icon: Stethoscope,
    text: "PVT Canada : teleconsultation medicale 100% prise en charge par ACS.",
    color: 'text-blue-500 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
];

export function WhyChooseSection({ title, content, sections }: WhyChooseSectionProps) {
  const { userInfo } = useAuth();

  return (
    <section className="py-12 sm:py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-14"
        >
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Les principaux avantages de Globe PVT
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            L'assurance preferee des Pvtistes avec d'excellents temoignages
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {advantages.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex items-start gap-4 p-4 sm:p-5 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-300"
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center`}>
                <item.icon className={`w-5 h-5 ${item.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed">
                  {item.text}
                </p>
                {item.link && (
                  <a
                    href={item.link.url}
                    onClick={item.link.url.includes('pvtistes.net/forum') ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      openWebRedirect(item.link!.url, userInfo);
                    } : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 group"
                  >
                    <span>{item.link.label}</span>
                    <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
